// Swift's `CollectionDifference` as `BidirectionalCollection.difference(from:)` makes it — the standard library's own
// Myers diff (`_myers(from:to:using:)`, CollectionDifference.swift / Diffing.swift), ported step for step so ties
// break the same way — and `inferringMoves()`, which pairs a removal and an insertion of an element that is removed
// once and inserted once. TextDiff and ProjectDiff depend on both.

export type Change<T> =
  | { type: 'remove'; offset: number; element: T; associatedWith: number | null }
  | { type: 'insert'; offset: number; element: T; associatedWith: number | null };

/** `_V`: Myers' V array, indexed −d…d (0 where never set). */
class V {
  private readonly a: Int32Array;
  constructor(maxIndex: number) {
    this.a = new Int32Array(maxIndex + 1);
  }
  private static t(i: number) {
    return i <= 0 ? -i : i - 1;
  }
  get(i: number) {
    return this.a[V.t(i)] ?? 0;
  }
  set(i: number, v: number) {
    this.a[V.t(i)] = v;
  }
}

/** `new.difference(from: old)` with `==` given by `equal`: removals (offsets in `old`) and insertions (in `new`). */
export function difference<T>(old: readonly T[], next: readonly T[], equal: (a: T, b: T) => boolean = (a, b) => a === b): Change<T>[] {
  const n = old.length, m = next.length;
  // _descent
  const trace: V[] = [];
  let v = new V(1);
  v.set(1, 0);
  let x = 0, y = 0;
  outer: for (let d = 0; d <= n + m; d++) {
    const prev = v;
    trace.push(v);
    v = new V(d);
    for (let k = -d; k <= d; k += 2) {
      if (k === -d) x = prev.get(k + 1);
      else {
        const km = prev.get(k - 1);
        if (k !== d) {
          const kp = prev.get(k + 1);
          x = km < kp ? kp : km + 1;
        } else x = km + 1;
      }
      y = x - k;
      while (x < n && y < m && equal(old[x], next[y])) {
        x++;
        y++;
      }
      v.set(k, x);
      if (x >= n && y >= m) break outer;
    }
  }
  // _formChanges
  const changes: Change<T>[] = [];
  x = n;
  y = m;
  for (let d = trace.length - 1; d > 0; d--) {
    const tv = trace[d];
    const k = x - y;
    const prevK = k === -d || (k !== d && tv.get(k - 1) < tv.get(k + 1)) ? k + 1 : k - 1;
    const prevX = tv.get(prevK);
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      x--;
      y--;
    }
    if (y !== prevY) changes.push({ type: 'insert', offset: prevY, element: next[prevY], associatedWith: null });
    else changes.push({ type: 'remove', offset: prevX, element: old[prevX], associatedWith: null });
    x = prevX;
    y = prevY;
  }
  return changes;
}

/** `inferringMoves()`: an element removed exactly once and inserted exactly once is a move (each names the other's
 *  offset). `key` must identify equal elements (Swift hashes them). */
export function inferringMoves<T>(changes: Change<T>[], key: (t: T) => string = (t) => String(t)): Change<T>[] {
  const unique = (type: 'remove' | 'insert') => {
    const out = new Map<string, number | null>();
    for (const c of changes) {
      if (c.type !== type) continue;
      const k = key(c.element);
      out.set(k, out.has(k) ? null : c.offset);
    }
    for (const [k, v] of out) if (v === null) out.delete(k);
    return out as Map<string, number>;
  };
  const removals = unique('remove'), insertions = unique('insert');
  return changes.map((c) => {
    const k = key(c.element);
    if (c.type === 'remove') {
      if (!removals.has(k)) return c;
      const to = insertions.get(k);
      return to !== undefined ? { ...c, associatedWith: to } : c;
    }
    if (!insertions.has(k)) return c;
    const from = removals.get(k);
    return from !== undefined ? { ...c, associatedWith: from } : c;
  });
}
